import type { ReactNode } from "react";

import { Button, PixelPlaceholderLoader } from "@similarweb/ui-components";

import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import type { IBoxTitleProps } from "pro-features/components/BoxTitle/src/BoxTitle";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { i18nFilter } from "app/filters/i18nFilter";
import { AssetsService } from "app/services/AssetsService";
import { allTrackers } from "app/services/track/track";

import {
    ColLoader,
    GridContainer,
    type INoDataContainerProps,
    LoaderContainer,
    MetricTitleText,
    NoDataContainer,
    NoDataSubTitle,
    NoDataTitle,
    SeeMoreContainer,
    SubTitleContainer,
    Text,
} from "../StyledComponents";

interface IMetricTitleProps extends Pick<IBoxTitleProps, "tooltip"> {
    fontSize?: number;
    headline: ReactNode;
}

export const MetricTitle = ({ tooltip, headline, fontSize = 20 }: IMetricTitleProps) => (
    <BoxTitle tooltip={tooltip}>
        <MetricTitleText fontSize={fontSize}>{headline}</MetricTitleText>
    </BoxTitle>
);

const KeywordMetricsSubTitle = ({ subtitle = "", webSource = "none" }) => {
    const subtitleFilters = {
        all: [
            {
                filter: "webSource",
                value: "Total",
            },
        ],
        Desktop: [
            {
                filter: "webSource",
                value: "desktop",
            },
        ],
        MobileWeb: [
            {
                filter: "webSource",
                value: "MobileWeb",
            },
        ],
        Total: [
            {
                filter: "webSource",
                value: "Total",
            },
        ],
    };
    return (
        <SubTitleContainer>
            <Text opacity={1}>{subtitle}</Text>&nbsp;
            {subtitleFilters[webSource] ? (
                <Text opacity={1}>
                    <BoxSubtitle filters={subtitleFilters[webSource]} />
                </Text>
            ) : null}
        </SubTitleContainer>
    );
};

export interface INoDataProps extends Pick<INoDataContainerProps, "paddingBottom" | "paddingTop"> {
    noDataTitleKey?: string;
    noDataSubTitleKey?: string;
    className?: string;
    Img?: ReactNode;
}

export const NoData = ({
    noDataTitleKey = "keyword.analysis.overview.nodata",
    noDataSubTitleKey = "keyword.analysis.overview.nodata.subtitle",
    paddingTop,
    paddingBottom,
    className = "",
    Img = (
        <img
            src={AssetsService.assetUrl("/images/no-data-new.svg")}
            style={{ height: "96px", width: "190px" }}
        />
    ),
}: INoDataProps) => {
    const i18n = i18nFilter();
    return (
        <NoDataContainer
            className={className}
            paddingTop={paddingTop}
            paddingBottom={paddingBottom}
            data-automation="no-data-wrapper"
        >
            {Img}
            <NoDataTitle data-automation-title-name="no-result-title">{i18n(noDataTitleKey)}</NoDataTitle>
            <NoDataSubTitle data-automation-subtitle-name="no-result-subtitle">
                {i18n(noDataSubTitleKey)}
            </NoDataSubTitle>
        </NoDataContainer>
    );
};

export const TableLoader: any = ({
    rowsAmount = 6,
    columnsAmount = 2,
    cellHeight = 12,
    isBorder = true,
    isCenter = false,
    cellWidth = "30%",
}) => (
    <LoaderContainer>
        {[...Array(rowsAmount)].map((x, i) => (
            <GridContainer
                key={i}
                isFirst={i === 0 || !isBorder}
                isCenter={isCenter}
            >
                {[...Array(columnsAmount)].map((y, j, items) => (
                    <ColLoader
                        key={j}
                        isLast={j === items.length - 1}
                        width={cellWidth}
                    >
                        <PixelPlaceholderLoader
                            width={"100%"}
                            height={cellHeight}
                        />
                    </ColLoader>
                ))}
            </GridContainer>
        ))}
    </LoaderContainer>
);
export const SeeMore = ({ children, componentName, innerLink, textAlign = "center" }) => {
    const onClick = (componentName) => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", `${componentName || ""}/see_more`);
    };
    return (
        <SeeMoreContainer
            textAlign={textAlign}
            onClick={() => onClick(componentName)}
        >
            <a
                href={innerLink}
                target="_self"
            >
                <Button type="flat">{children}</Button>
            </a>
        </SeeMoreContainer>
    );
};
